import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { DateFieldFr } from '@/components/ui/date-field-fr';

const CAT = { confirmee: ['Perte confirmée (photo de fin)', 'bg-red-100 text-red-800'], incertaine: ['Incertain (photo de début seule)', 'bg-amber-100 text-amber-800'] };
const err = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur réseau');
const fr = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR');
const PAGE = 50;

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
function exportCsv(items) {
  const head = ['Salarié', 'Date', 'Catégorie', 'Photos', 'Tournée déclarée', 'Site déclaré', 'Livrés', 'Planning publié', 'Informations manquantes'];
  const lines = items.map((i) => [i.user_name, i.date, CAT[i.category][0], i.evidence.map((e) => e.type).join('+'),
    [...new Set(i.evidence.map((e) => e.tour_type).filter(Boolean))].join(' / '), [...new Set(i.evidence.map((e) => e.site_name).filter(Boolean))].join(' / '),
    i.evidence.find((e) => e.delivered != null)?.delivered, i.published_planning?.status, i.missing_info.join(' ; ')].map(csvCell).join(';'));
  const blob = new Blob([`\ufeff${[head.map(csvCell).join(';'), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'journees_a_examiner.csv'; a.click();
}

const Item = ({ i }) => (
  <div className="rounded border bg-white p-2 text-xs" data-testid={`evidence-${i.user_id}-${i.date}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-medium">{i.user_name} — {fr(i.date)}</span>
      <span className={`rounded px-2 py-0.5 font-semibold ${CAT[i.category][1]}`}>{CAT[i.category][0]}</span>
      {!i.employee_active && <span className="rounded bg-slate-200 px-2">salarié désactivé</span>}
      {i.duplicate_records > 0 && <span className="rounded bg-slate-100 px-2">{i.duplicate_records} photo(s) en double</span>}
      <span className="ml-auto text-slate-500">Site actuel : {i.employee_site || '—'}</span>
    </div>
    <ul className="mt-1 space-y-0.5">
      {i.evidence.map((e, n) => (
        <li key={n}>Photo {e.type === 'end' ? 'de fin' : 'de début'}{e.at ? ` · ${new Date(e.at).toLocaleString('fr-FR')}` : ''}
          {e.tour_type ? ` · tournée déclarée « ${e.tour_type} »` : ''}{e.site_name ? ` · site « ${e.site_name} »` : ''}
          {e.delivered != null ? ` · ${e.delivered} livrés / ${e.returned ?? 0} retours` : ''}{e.vehicle ? ` · ${e.vehicle}` : ''}</li>
      ))}
      {i.published_planning && <li>Planning publié : statut « {i.published_planning.status || '—'} »{i.published_planning.loading_time ? ` · chargement ${i.published_planning.loading_time}` : ''}</li>}
      {i.legacy_hours && <li>Heures saisies autrefois sur un poste : {i.legacy_hours.join(' / ')} h (non appliquées)</li>}
    </ul>
    <p className="mt-1 text-red-700">Manquant : {i.missing_info.join(' · ')}</p>
  </div>
);

export const PhotoEvidenceReview = () => {
  const [range, setRange] = useState({ start: '2026-02-01', end: '2026-06-30', category: '' });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(null);
  const fetchPage = async (q0, p, size = PAGE) => {
    const q = new URLSearchParams({ start_date: q0.start, end_date: q0.end, page: String(p), page_size: String(size) });
    if (q0.category) q.set('category', q0.category);
    return (await axios.get(`${API}/schedule/integrity/photo-evidence?${q}`)).data;
  };
  const search = async (p = 1, q0 = query) => {
    try { setData(await fetchPage(q0, p)); setPage(p); setQuery(q0); } catch (e) { toast.error(err(e)); }
  };
  const exportAll = async () => {
    try {
      const all = []; let p = 1; let res;
      do { res = await fetchPage(query, p, 200); all.push(...res.items); p += 1; } while (all.length < res.total && res.items.length);
      exportCsv(all);
    } catch (e) { toast.error(err(e)); }
  };
  const s = data?.summary;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <section data-testid="photo-evidence-section">
      <h2 className="mb-1 text-lg font-semibold">Journées attestées par photo, absentes du planning (à examiner)</h2>
      <p className="mb-2 text-sm text-slate-600">Lecture seule. Rien n’est recréé ni intégré à la paie : une photo prouve une présence, pas les heures, la tournée ni la validation.</p>
      <div className="flex flex-wrap items-end gap-2">
        <DateFieldFr value={range.start} onChange={(v) => setRange({ ...range, start: v })} testid="evidence-start" />
        <DateFieldFr value={range.end} onChange={(v) => setRange({ ...range, end: v })} testid="evidence-end" />
        <select value={range.category} onChange={(e) => setRange({ ...range, category: e.target.value })} className="h-9 rounded border px-2 text-sm" data-testid="evidence-category">
          <option value="">Toutes</option><option value="confirmee">Pertes confirmées</option><option value="incertaine">Cas incertains</option>
        </select>
        <Button onClick={() => search(1, range)} data-testid="evidence-search-btn">Rechercher</Button>
        {data?.total > 0 && <Button variant="outline" onClick={exportAll} data-testid="evidence-export-btn">Exporter la liste (CSV)</Button>}
      </div>
      {s && (
        <p className="mt-2 text-sm" data-testid="evidence-summary">
          {s.session_records} photo(s) · {s.unique_days_with_photo} journée(s) unique(s) avec photo · {s.days_with_planning} déjà au planning ·{' '}
          <b>{s.confirmed_lost} perte(s) confirmée(s)</b> · {s.uncertain} incertaine(s) · {s.duplicate_records} photo(s) en double
        </p>
      )}
      <div className="mt-2 space-y-2">{(data?.items || []).map((i) => <Item key={`${i.user_id}|${i.date}`} i={i} />)}</div>
      {pages > 1 && (
        <div className="mt-2 flex items-center gap-3 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => search(page - 1)} data-testid="evidence-prev">Précédent</Button>
          <span>Page {page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => search(page + 1)} data-testid="evidence-next">Suivant</Button>
        </div>
      )}
    </section>
  );
};
